import React, { useCallback, useMemo, useState } from 'react';
import { useRubricsStats, useRubricsPercentageStats } from '../state/hooks';
import { useClassInsights } from './ClassInsightsContext';
import { statsBody } from './useFilters';
import { catalogClasses } from './catalogSelectors';
import { PanelHeader } from './PanelHeader';
import { PanelIconButton } from './PanelIconButton';
import { ExportFormatDropdown } from './ExportFormatDropdownNew';
import { ExpandIcon } from './Icons';
import { RubricsModal } from './RubricsModal';
import DropdownMenu from '../DropdownMenu';
import SmallLoader from '../Loader/SmallLoader';
import { getTierClass, getRankClass, getTaskHeaderLabel } from './utils';
import { SECTIONS } from './DownloadManager';
import DownLoad from '../../static/img/Download.svg';
import rubricIcon from '../../static/icons/black-rubric-icon.svg';
import blackTableIcon from '../../static/icons/black-table-icon.svg';
import blackGraphIcon from '../../static/icons/black-graph-icon.svg';
import whiteTableIcon from '../../static/icons/white-table-icon.svg';
import whiteGraphIcon from '../../static/icons/white-graph-icon.svg';
import QuestionTooltip from '../../components2/QuestionTooltip';

const TASK_COL_WIDTH = 80;
const SECTION_MIN_WIDTH = 280;
const getSectionWidth = (criteria) =>
  Math.max(SECTION_MIN_WIDTH, (criteria?.tasks?.length || 0) * TASK_COL_WIDTH);

export function RubricsPanel({
  mode = 'small',
  onExpand,
  onCollapse,
  open = true,
  onExited,
  exportMode = false,
}) {
  const { filters, debouncedFilters, setFilters, catalog } = useClassInsights();
  const [viewMode, setViewMode] = useState('table');
  const [sortBy, setSortBy] = useState('Performance - High to low');

  const sortOrder = sortBy === 'Performance - High to low' ? 'desc' : 'asc';

  // JED-1066: empty classIds means "all classes", not "no classes". Gate the
  // query on whether the catalog has any class at all -- mirroring
  // KpiSummaryCards -- so selecting "All Classes" still loads this report
  // instead of leaving the panel blank while the KPIs show data.
  const hasClassScope = catalogClasses(catalog).length > 0;
  const { data, isFetching: isTileLoading } = useRubricsStats(
    { ...tileBody(debouncedFilters, debouncedFilters.rubricIds), sortBy: 'performance', sortOrder },
    hasClassScope
  );

  const list = data?.options || [];
  const stats = data?.stats || [];

  // Compute valid selection from current filter
  const getSelectedIds = () => {
    if (list.length === 0) return [];

    if (filters.rubricIds && filters.rubricIds.length > 0) {
      const validIds = filters.rubricIds.filter((id) =>
        list.some((item) => String(item.id) === String(id))
      );
      if (validIds.length > 0) {
        return [String(validIds[0])];
      }
    }

    return [String(list[0].id)];
  };
  const selectedIds = getSelectedIds();

  // Clean up stale URL IDs when scope changes. Skip when URL is empty (don't seed).
  const currentFilterIds = (filters.rubricIds || []).join(',');
  const validFilterIds = selectedIds.join(',');
  if (list.length > 0 && currentFilterIds && currentFilterIds !== validFilterIds) {
    setFilters({ rubricIds: selectedIds });
  }

  const {
    data: percentageStats,
    isFetching: isPercentageTileLoading,
  } = useRubricsPercentageStats(
    tileBody(debouncedFilters, selectedIds),
    hasClassScope
  );

  const onChange = useCallback((ids) => {
    const nextId = ids && ids.length > 0 ? String(ids[0]) : null;
    setFilters({ rubricIds: nextId ? [nextId] : [] });
  }, [setFilters]);
  const dropdownUiProps = {
    triggerFontSize: '16px',
    triggerPadding: '10px 16px',
  };

  const sortOptions = [
    'Performance - High to low',
    'Performance - Low to high',
  ];

  const normalizedPercentageStats = useMemo(() => {
    const selectedRubricId = selectedIds[0] ? String(selectedIds[0]) : null;
    const baseStats = Array.isArray(percentageStats)
      ? percentageStats
      : Array.isArray(percentageStats?.stats)
        ? percentageStats.stats
        : [];

    if (!selectedRubricId) return baseStats;
    const matchedStats = baseStats.filter(
      (item) => String(item?.rubricId || '') === selectedRubricId
    );
    return matchedStats.length > 0 ? matchedStats : baseStats;
  }, [percentageStats, selectedIds]);

  const filteredStats = useMemo(() => {
    if (!Array.isArray(stats) || stats.length === 0) return [];
    const allowedTaskIds = new Set();
    normalizedPercentageStats.forEach((rubricItem) => {
      (rubricItem.tasks || []).forEach((task) => {
        if (task?.taskId) allowedTaskIds.add(String(task.taskId));
      });
    });
    if (allowedTaskIds.size === 0) return stats;
    const nextStats = stats.filter((task) => allowedTaskIds.has(String(task?.taskId || '')));
    return nextStats.length > 0 ? nextStats : stats;
  }, [stats, normalizedPercentageStats]);

  const { taskGroups, rubricRows, questionNumbers } = useMemo(() => {
    if (!Array.isArray(filteredStats) || filteredStats.length === 0) {
      return { taskGroups: [], rubricRows: [], questionNumbers: [null] };
    }

    const selectedRubricId = selectedIds[0] ? String(selectedIds[0]) : null;

    const taskMap = new Map();
    const studentMap = new Map();
    const questionNumberSet = new Set();

    filteredStats.forEach((task) => {
      const taskId = task.taskId;
      const taskTitle = task.taskTitle || task.title || `Task ${taskId}`;

      (task.stats || []).forEach((stat) => {
        if (selectedRubricId && String(stat.rubricId || '') !== selectedRubricId) {
          return;
        }

        const questionKey = stat.questionSerialNumber?.value || 'unknown';
        const questionNumber = Number(questionKey) || 0;
        if (questionNumber > 0) questionNumberSet.add(questionNumber);

        const scores = (stat.criteriaWithLevels || []).map((criteria, index) => ({
          value: criteria.selectedLevel?.value || '',
          tier: criteria.selectedLevel?.tier || '',
          criteriaTitle: criteria.criteriaTitle || `Criterion ${index + 1}`,
        }));

        if (!taskMap.has(taskId)) {
          taskMap.set(taskId, { taskId, taskTitle, criterias: [] });
        }
        const taskEntry = taskMap.get(taskId);
        const nextTitles = scores.map((score, index) => score.criteriaTitle || `Criterion ${index + 1}`);
        if (nextTitles.length > taskEntry.criterias.length) {
          taskEntry.criterias = nextTitles;
        }

        const studentName = stat.studentName || 'Unknown';
        if (!studentMap.has(studentName)) {
          studentMap.set(studentName, {
            name: studentName,
            scoresByTaskQuestion: new Map(),
          });
        }
        studentMap.get(studentName).scoresByTaskQuestion.set(`${taskId}-${questionNumber}`, scores);
      });
    });

    const taskGroups = Array.from(taskMap.values());

    const rubricRows = Array.from(studentMap.values()).map((student, index) => ({
      rank: index + 1,
      name: student.name,
      scoresByTaskQuestion: student.scoresByTaskQuestion,
    }));

    const questionNumbers = Array.from(questionNumberSet).sort((a, b) => a - b);

    return {
      taskGroups,
      rubricRows,
      questionNumbers: questionNumbers.length > 0 ? questionNumbers : [null],
    };
  }, [filteredStats, selectedIds]);

  const criteriaBased = useMemo(() => {
    if (!Array.isArray(normalizedPercentageStats) || normalizedPercentageStats.length === 0) {
      return [];
    }

    const criteriaMap = new Map();

    normalizedPercentageStats.forEach((rubricItem) => {
      const tasks = rubricItem.tasks || [];
      tasks.forEach((task) => {
        const taskName = task.taskTitle || 'Untitled Task';
        const taskId = task.taskId || taskName;

        (task.criterias || []).forEach((criteria) => {
          const criteriaName = criteria.title || 'Untitled Criteria';
          const criteriaKey = criteriaName;

          if (!criteriaMap.has(criteriaKey)) {
            criteriaMap.set(criteriaKey, { criteriaName, tasks: [] });
          }

          const percentages = criteria.studentSharePercentages || {};
          criteriaMap.get(criteriaKey).tasks.push({
            taskName,
            taskId,
            high: Number(percentages.top) || 0,
            middle: Number(percentages.middle) || 0,
            low: Number(percentages.bottom) || 0,
          });
        });
      });
    });

    return Array.from(criteriaMap.values());
  }, [normalizedPercentageStats]);

  const rubricLegends = useMemo(() => {
    const firstWithLegends = normalizedPercentageStats.find(
      (item) => Array.isArray(item?.legends) && item.legends.length > 0
    );
    if (!firstWithLegends) return [];
    return [...new Set(firstWithLegends.legends.map((legend) => String(legend || '').trim()).filter(Boolean))];
  }, [normalizedPercentageStats]);

  const legendLevels = useMemo(() => {
    if (rubricLegends.length === 0) {
      return {
        high: ['A'],
        middle: ['B', 'C'],
        low: ['D', 'E'],
      };
    }

    const high = rubricLegends.slice(0, 1);
    const low = rubricLegends.slice(-2);
    const middle = rubricLegends.slice(1, -2);

    return {
      high,
      middle,
      low,
    };
  }, [rubricLegends]);

  const filteredCriteriaBased = criteriaBased;

  const maxGraphValue = 100;

  const csv = rubricRows.flatMap((row) =>
    questionNumbers.map((questionNumber) => {
      const flatScores = {};
      taskGroups.forEach((task) => {
        const scores = row.scoresByTaskQuestion.get(`${task.taskId}-${questionNumber}`) || [];
        const criteriaCount = Math.max(task.criterias.length, scores.length);
        for (let i = 0; i < criteriaCount; i += 1) {
          const score = scores[i];
          const criteriaTitle = task.criterias[i] || score?.criteriaTitle || `Score ${i + 1}`;
          const columnName = `${task.taskTitle} - ${criteriaTitle}`;
          flatScores[columnName] = score?.value || '';
        }
      });
      return {
        Rank: row.rank,
        Student: row.name,
        Question: getQuestionSerialLabel(questionNumber),
        ...flatScores,
      };
    })
  );

  if (mode === 'expanded') {
    return (
      <RubricsModal
        open={open}
        onClose={onCollapse}
        onExited={onExited}
        list={list}
        selectedIds={selectedIds}
        onChange={onChange}
        stats={filteredStats}
        percentageStats={normalizedPercentageStats}
        isLoading={isTileLoading}
        isLoadingPercentage={isPercentageTileLoading}
        sortBy={sortBy}
        onSortChange={setSortBy}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        exportMode={exportMode}
      />
    );
  }

  return (
    <section className="ci-panel ci-table-panel" data-section="rubrics">
      <PanelHeader title="Rubrics">
        <DropdownMenu
          multiselect
          singleSelect
          options={list}
          selectedValues={selectedIds.length > 0 ? [selectedIds[0]] : []}
          onChange={(values) =>
            onChange(
              values && values[0] != null ? [String(values[0])] : []
            )
          }
          placeholder="Select rubric"
          getOptionLabel={(opt) => opt.title || opt.name || 'Untitled Rubric'}
          getOptionValue={(opt) => String(opt.id)}
          alwaysWhite
          showAllOption={false}
          showCheckboxes
          showSearch
          leadingIcon={rubricIcon}
          compactDisplay
          triggerWidth="clamp(120px, 40vw, 350px)"
          {...dropdownUiProps}
          dropdownHeader="Rubrics"
          dropdownSubtitle="Select rubric to show data for"
        />
        <ExportFormatDropdown
          section={SECTIONS.RUBRICS}
          data={filteredStats}
          csvData={csv}
          excludedFormatKeys={viewMode === 'graph' ? ['pdf'] : []}
          isModal={false}
          trigger={
            <PanelIconButton aria-label="Download">
              <img src={DownLoad} className="ci-download-icon" alt="" />
            </PanelIconButton>
          }
        />
        <PanelIconButton
          type="button"
          onClick={onExpand}
          aria-label="Expand"
          className="ci-icon-button-expand"
        >
          <ExpandIcon />
        </PanelIconButton>
      </PanelHeader>
      <div className="ci-sort-controls">
        {viewMode === 'table' && !isTileLoading && rubricRows.length > 0 && (
          <>
            <span className="ci-sort-label">Sort by:</span>
            <DropdownMenu
              multiselect
              singleSelect
              options={sortOptions}
              selectedValues={[sortBy]}
              onChange={(values) => setSortBy(values[0] || sortOptions[0])}
              placeholder="Sort"
              getOptionLabel={(opt) => opt}
              getOptionValue={(opt) => opt}
              alwaysWhite
              compactDisplay
              showCheckboxes={false}
              showAllOption={false}
              triggerFontSize="14px"
              triggerPadding="8px 12px"
              triggerWidth="auto"
              triggerMinWidth="280px"
            />
          </>
        )}
        <div className="ci-view-toggles">
          <button
            className={`ci-view-toggle ${viewMode === 'table' ? 'active' : ''}`.trim()}
            type="button"
            aria-label="Table view"
            onClick={() => setViewMode('table')}
          >
            <img src={viewMode === 'table' ? whiteTableIcon : blackTableIcon} className="ci-grid-list-icon" alt="" />
          </button>
          <button
            className={`ci-view-toggle ${viewMode === 'graph' ? 'active' : ''}`.trim()}
            type="button"
            aria-label="Graph view"
            onClick={() => setViewMode('graph')}
          >
            <img src={viewMode === 'graph' ? whiteGraphIcon : blackGraphIcon} className="ci-grid-list-icon" alt="" />
          </button>
        </div>
      </div>
      {viewMode === 'table' ? (
        <div className="ci-table-wrap ci-rubric-panel-wrap">
          {isTileLoading ? (
            <div className="ci-panel-loading">
              <SmallLoader />
            </div>
          ) : rubricRows.length === 0 ? (
            <div className="ci-empty-state">No data available</div>
          ) : (
            <table className="ci-table ci-rubric-panel-table">
              <thead>
                <tr>
                  <th className="ci-sticky-col">Student</th>
                  {taskGroups.length > 0 && <th className="ci-group-separator-cell" aria-hidden="true" />}
                  <th
                    className="ci-rubric-question-serial-header"
                    aria-hidden="true"
                  />
                  <th className="ci-rubric-question-gap-cell" aria-hidden="true" />
                  {taskGroups.map((task, taskIndex) => {
                    const { full, display } = getTaskHeaderLabel(task.taskTitle);
                    const singleTask = taskGroups.length === 1;
                    return (
                    <React.Fragment key={`task-${task.taskId}`}>
                      <th
                        colSpan={Math.max(1, task.criterias.length)}
                        className="ci-question-group-header"
                      >
                        <div
                          className={
                            singleTask
                              ? 'ci-task-group-header-title-wrap ci-task-group-header-title-wrap--single'
                              : 'ci-task-group-header-title-wrap'
                          }
                        >
                          <QuestionTooltip text={full}>
                            <span className="ci-task-group-header-title">
                              {singleTask ? full : display}
                            </span>
                          </QuestionTooltip>
                        </div>
                      </th>
                      {taskIndex < taskGroups.length - 1 && (
                        <th className="ci-group-separator-cell" aria-hidden="true" />
                      )}
                    </React.Fragment>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rubricRows.map((row) =>
                  questionNumbers.map((questionNumber, rowIndex) => (
                    <tr key={`${row.name}-${questionNumber ?? 'q'}`}>
                      {rowIndex === 0 && (
                        <td className="ci-sticky-col" rowSpan={questionNumbers.length}>
                          <div className="ci-table-student">
                            <span className={getRankClass(row.rank)}>{row.rank}</span>
                            {row.name}
                          </div>
                        </td>
                      )}
                      {taskGroups.length > 0 && <td className="ci-group-separator-cell" aria-hidden="true" />}
                      <td className="ci-rubric-question-serial-cell">
                        <span className="ci-rubric-question-serial-label">
                          {getQuestionSerialLabel(questionNumber)}
                        </span>
                      </td>
                      <td className="ci-rubric-question-gap-cell" aria-hidden="true" />
                      {taskGroups.map((task, taskIndex) => {
                        const criteriaCount = Math.max(1, task.criterias.length);
                        const scores = row.scoresByTaskQuestion.get(`${task.taskId}-${questionNumber}`) || [];
                        return (
                          <React.Fragment key={`${row.name}-${questionNumber ?? 'q'}-${task.taskId}`}>
                            {Array.from({ length: criteriaCount }).map((_, scoreIndex) => {
                              const score = scores[scoreIndex];
                              const value = score?.value || '-';
                              const tier = score?.tier || '';
                              const criteriaTitle = task.criterias[scoreIndex] || score?.criteriaTitle || 'Criterion';
                              const classes = [getTierClass(tier), 'ci-rubric-score-cell'];
                              if (scoreIndex === 0) classes.push('ci-rubric-score-cell--group-start');
                              if (scoreIndex === criteriaCount - 1) classes.push('ci-rubric-score-cell--group-end');
                              return (
                                <td
                                  key={`${task.taskId}-${questionNumber ?? 'q'}-${scoreIndex}`}
                                  className={classes.join(' ')}
                                >
                                  <QuestionTooltip text={criteriaTitle}>
                                    <span className="ci-rubric-score-value">{value}</span>
                                  </QuestionTooltip>
                                </td>
                              );
                            })}
                            {taskIndex < taskGroups.length - 1 && (
                              <td className="ci-group-separator-cell" aria-hidden="true" />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div className="ci-table-wrap ci-rubric-panel-wrap">
          <div className="ci-rubric-graph-wrap">
            <div className="ci-rubric-graph-scroll">
              <div className="ci-rubric-graph-area">
                {!isPercentageTileLoading && filteredCriteriaBased.length > 0 && (
                  <>
                    <div className="ci-rubric-y-label">Share of students (%)</div>
                    <div className="ci-rubric-y-axis">
                      <span>{maxGraphValue}</span>
                      <span>{Math.floor(maxGraphValue * 0.75)}</span>
                      <span>{Math.floor(maxGraphValue * 0.5)}</span>
                      <span>{Math.floor(maxGraphValue * 0.25)}</span>
                      <span>0</span>
                    </div>
                  </>
                )}
                <div className="ci-rubric-graph-content" style={{
                  flex: '0 0 auto',
                  minWidth: filteredCriteriaBased.length > 0
                    ? `${filteredCriteriaBased.reduce((sum, criteria) => sum + getSectionWidth(criteria), 0)}px`
                    : 'auto',
                  width: filteredCriteriaBased.length > 0
                    ? `${filteredCriteriaBased.reduce((sum, criteria) => sum + getSectionWidth(criteria), 0)}px`
                    : 'auto'
                }}>
                {filteredCriteriaBased.length > 0 && !isPercentageTileLoading && (
                  <div className="ci-rubric-task-headers" style={{
                    display: 'flex',
                    minWidth: `${filteredCriteriaBased.reduce((sum, criteria) => sum + getSectionWidth(criteria), 0)}px`
                  }}>
                    {filteredCriteriaBased.map((criteria) => (
                      <div key={criteria.criteriaName} className="ci-rubric-task-header-item" style={{ width: getSectionWidth(criteria), fontSize: '14px', fontWeight: 400, flexShrink: 0 }}>
                        {criteria.criteriaName}
                      </div>
                    ))}
                  </div>
                )}
                <div className="ci-rubric-graph-container" style={{
                  minWidth: filteredCriteriaBased.length > 0
                    ? `${filteredCriteriaBased.reduce((sum, criteria) => sum + getSectionWidth(criteria), 0)}px`
                    : 'auto'
                }}>
                  {isPercentageTileLoading ? (
                    <div className="ci-panel-loading">
                      <SmallLoader />
                    </div>
                  ) : filteredCriteriaBased.length === 0 ? (
                    <div className="ci-empty-state">No data available</div>
                  ) : (
                    filteredCriteriaBased.map((criteria, criteriaIndex) => {
                      const colors = ['#F8EBEB', '#E7FCF9', '#E7EDFC'];
                      const bgColor = colors[criteriaIndex % colors.length];

                      return (
                        <div
                          key={criteria.criteriaName}
                          className="ci-rubric-task-section"
                          style={{
                            backgroundColor: bgColor,
                            width: getSectionWidth(criteria),
                            flexShrink: 0,
                          }}
                        >
                          <div className="ci-rubric-task-bars-container">
                            {criteria.tasks.map((task, taskIndex) => {
                              const maxHeight = 200;
                              const highHeight = maxGraphValue > 0 ? (task.high / maxGraphValue) * maxHeight : 0;
                              const middleHeight = maxGraphValue > 0 ? (task.middle / maxGraphValue) * maxHeight : 0;
                              const lowHeight = maxGraphValue > 0 ? (task.low / maxGraphValue) * maxHeight : 0;

                              return (
                                <div
                                  key={`${criteria.criteriaName}-${task.taskName}-${taskIndex}`}
                                  className="ci-rubric-criteria-column"
                                >
                                  <div className="ci-rubric-graph-bars">
                                    <div className="ci-rubric-bar ci-rubric-bar-high" style={{ height: `${highHeight}px` }} />
                                    <div className="ci-rubric-bar ci-rubric-bar-middle" style={{ height: `${middleHeight}px` }} />
                                    <div className="ci-rubric-bar ci-rubric-bar-low" style={{ height: `${lowHeight}px` }} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                {filteredCriteriaBased.length > 0 && !isPercentageTileLoading && (
                  <div className="ci-rubric-criteria-labels-row" style={{
                    display: 'flex',
                    minWidth: `${filteredCriteriaBased.reduce((sum, criteria) => sum + getSectionWidth(criteria), 0)}px`
                  }}>
                    {filteredCriteriaBased.map((criteria) => (
                      <div key={`labels-${criteria.criteriaName}`} className="ci-rubric-task-criteria-labels" style={{ width: getSectionWidth(criteria), flexShrink: 0 }}>
                        {criteria.tasks.map((task, taskIndex) => (
                          <div
                            key={`label-${criteria.criteriaName}-${task.taskName}-${taskIndex}`}
                            className="ci-rubric-criteria-label-item"
                            title={task.taskName}
                          >
                            {truncateText(task.taskName, 20)}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              </div>
            </div>
            {filteredCriteriaBased.length > 0 && !isPercentageTileLoading && (
              <div className="ci-rubric-x-label">Tasks</div>
            )}
            <div className="ci-rubric-graph-legend">
              {legendLevels.high.length > 0 && (
                <span>
                  <span className="ci-rubric-legend-dot ci-rubric-legend-high" /> {legendLevels.high.join(', ')}
                </span>
              )}
              {legendLevels.middle.length > 0 && (
                <span>
                  <span className="ci-rubric-legend-dot ci-rubric-legend-middle" /> {legendLevels.middle.join(', ')}
                </span>
              )}
              {legendLevels.low.length > 0 && (
                <span>
                  <span className="ci-rubric-legend-dot ci-rubric-legend-low" /> {legendLevels.low.join(', ')}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

const truncateText = (name, maxLength = 25) => {
  if (name.length <= maxLength) return name;
  return name.substring(0, maxLength) + '...';
};

const getQuestionSerialLabel = (questionNumber) => {
  const serial = Number(questionNumber);
  if (!Number.isFinite(serial) || serial <= 0) return 'Q-';
  return `Q${serial}`;
};

const tileBody = (filters, ids) => ({
  ...statsBody(filters),
  markingToolIds: ids && ids.length > 0 ? ids : null,
});
